import { useEffect, useState } from "react";
import styled from "styled-components";
import {
	Auth,
	CoorporateScheme,
	ErrorScheme,
	Regex,
	UserScheme,
} from "client-v2";
import media from "styled-media-query";
import { useSelector } from "react-redux";
import { State } from "../../../redux";

import {
	Input,
	Text,
	Checkbox,
	Color,
	ColorV2,
	InputBirthday,
	Modal,
	Button,
} from "@adoptaunabuelo/react-components";
import dayjs from "dayjs";
import SectionContainer from "../../../components/container/SectionContainer";
import LetterMail from "../../../assets/images/background/LetterMail";

const FatherContainer = styled.div<{ $hidden: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding-top: 110px;
	padding-bottom: 40px;
	padding-right: 100px;
	transition: transform 1s ease-in-out;
	transform: ${(props) =>
		props.$hidden
			? `translateX(-${window.screen.width / 2}px) translateY(300px)`
			: "translateX(0px) translateY(0px)"};
	${(props) => media.lessThan("medium")`
		margin-top: 12px;
        padding: 48px 24px 8px;
		transition: transform .5s ease-in-out;
		transform: ${
			props.$hidden
				? `translateX(-${window.screen.width}px) translateY(200px)`
				: "translateX(0px) translateY(0px)"
		};
	`}
`;
const TermsText = styled(Text)`
	font-size: 13px !important;
	color: ${Color.text.high};
`;
const Link = styled.a`
	color: ${Color.text.high};
	&:hover {
		text-decoration: underline;
	}
`;
const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 16px;
	z-index: 5;
	padding: 16px 0px;
	${media.lessThan("medium")`
        padding: 0px 0px 16px;
	`}
`;
const Background = styled.div`
	position: absolute;
	top: 0px;
	right: 0px;
	height: 100%;
	width: 100vw;
	background-color: white;
	box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	${media.lessThan("medium")`
        right: unset;
		left: 0px;
	`}
`;
const CheckboxView = styled.div`
	display: flex;
	flex: 1;
	align-items: flex-end;
	padding-top: 24px;
	${media.lessThan("medium")`
        padding-top: 16px;
	`}
`;

const Personal = (props: PersonalProps) => {
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const colors =
		props.corporate && props.corporate.color
			? {
					buttonBackground: props.corporate.color.main,
					buttonColor: props.corporate.color.third,
					animation: [
						props.corporate.color.main,
						props.corporate.color.secondary,
					],
			  }
			: {
					buttonBackground: ColorV2.surface.primary,
					buttonColor: "white",
					animation: ["#00315C", "#005AA8"],
			  };

	const [selection, setSelection] = useState({
		name: props.user.name,
		surname: props.user.surname,
		email: props.user.email,
		terms: false,
		birthday: props.user.birthday
			? new Date(props.user.birthday.iso)
			: undefined,
	});
	const [errors, setErrors] = useState<{
		name: string | undefined;
		surname: string | undefined;
		email: string | undefined;
		terms: string | undefined;
		birthday: string | undefined;
	}>({
		name: undefined,
		surname: undefined,
		email: undefined,
		terms: undefined,
		birthday: undefined,
	});
	const [loading, setLoading] = useState(false);
	const [show, setShow] = useState(false);
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		setShow(true);
		return () => {
			setShow(false);
		};
	}, []);

	const checkData = () => {
		if (
			selection.name &&
			selection.surname &&
			selection.email &&
			selection.terms &&
			selection.birthday
		) {
			return true;
		} else {
			setErrors({
				name: !selection.name
					? translation.component_input_name_error
					: undefined,
				surname: !selection.surname
					? translation.component_input_surname_error
					: undefined,
				email: !selection.email
					? translation.component_input_email_error
					: undefined,
				terms: !selection.terms
					? translation.component_input_terms_error
					: undefined,
				birthday: !selection.birthday
					? translation.component_input_birth_error
					: undefined,
			});
			return false;
		}
	};

	const onNextClick = () => {
		const data = checkData();
		if (data) {
			setShowModal(true);
		}
	};

	const onModalClose = () => {
		setShowModal(false);
		const data = checkData();
		if (data) {
			const emailIsValid = Regex.email(selection.email);
			if (emailIsValid) {
				setErrors({
					name: undefined,
					surname: undefined,
					email: undefined,
					terms: undefined,
					birthday: undefined,
				});
				setLoading(true);
				Auth.register(props.user, {
					name: selection.name,
					surname: selection.surname,
					email: selection.email as any,
					birthday: selection.birthday,
					coorporateId: props.corporate?.objectId,
				})
					.then((result) => {
						setLoading(false);
						props.onFinish({ data: result.data });
					})
					.catch((e: ErrorScheme) => {
						setLoading(false);
						props.onFinish({ error: e.message });
					});
			} else {
				if (!emailIsValid)
					setErrors({
						...errors,
						email: translation.component_input_email_error_02,
					});
				return undefined;
			}
		}
	};

	const onInputChange = (input: string, value: string) => {
		if (input === "name") {
			setSelection({
				...selection,
				name: value,
			});
			setErrors({
				...errors,
				name: undefined,
			});
		} else if (input === "surname") {
			setSelection({
				...selection,
				surname: value,
			});
			setErrors({
				...errors,
				surname: undefined,
			});
		} else if (input === "email") {
			setSelection({
				...selection,
				email: value,
			});
			setErrors({
				...errors,
				email: undefined,
			});
		} else if (input === "birthday") {
			setSelection({
				...selection,
				birthday: new Date(value),
			});
			setErrors({
				...errors,
				birthday: undefined,
			});
		}
	};

	const onTermsChange = (check: Array<any>) => {
		setErrors({
			...errors,
			terms: undefined,
		});
		if (check.length > 0)
			setSelection({
				...selection,
				terms: true,
			});
		else
			setSelection({
				...selection,
				terms: false,
			});
	};

	return (
		<FatherContainer $hidden={!show}>
			<Modal
				isVisible={showModal}
				hideClose={true}
				type={isMobile ? "full-screen" : "default"}
				contentStyle={{ padding: "24px 24px 12px" }}
				onClose={() => setShowModal(false)}
			>
				<Text type="p2" style={{ textAlign: "center" }}>
					Solo por confirmar,
				</Text>
				<Text
					type="h4"
					weight="semibold"
					style={{ textAlign: "center", marginBottom: 32 }}
				>
					¿tienes {dayjs().diff(dayjs(selection.birthday), "years")}{" "}
					años?
				</Text>
				<Button style={{ width: "100%" }} onClick={onModalClose}>
					Confirmar
				</Button>
				<Button
					style={{
						width: "100%",
						color: ColorV2.text.neutralHard,
						backgroundColor: "transparent",
					}}
					onClick={() => setShowModal(false)}
				>
					Cambiar fecha
				</Button>
			</Modal>
			<Background>
				{!isMobile && (
					<LetterMail
						style={{
							position: "absolute",
							top: 0,
							right: 0,
							width: "100%",
						}}
					/>
				)}
			</Background>
			<SectionContainer
				style={{ paddingTop: 0 }}
				childrenProps={{
					style: { flex: 1, margin: 0 },
				}}
				buttonProps={{
					style: {
						backgroundColor: colors.buttonBackground,
						color: colors.buttonColor,
					},
					loading: loading,
					children: translation.component_button_next,
					onClick: onNextClick,
				}}
			>
				<Container>
					<Text
						type="p"
						style={{
							fontFamily: "DM Mono",
							textTransform: "uppercase",
							marginBottom: 8,
							color: ColorV2.text.neutralMedium,
						}}
					>
						REMITENTE
					</Text>
					<Input
						type="text"
						defaultValue={selection.name}
						placeholder={
							translation.component_input_name_placeholder
						}
						error={errors.name}
						design={"third"}
						onChange={(e: any) =>
							onInputChange("name", e.target.value)
						}
					/>
					<Input
						type="text"
						defaultValue={selection.surname}
						placeholder={
							translation.component_input_surname_placeholder
						}
						error={errors.surname}
						design={"third"}
						onChange={(e: any) =>
							onInputChange("surname", e.target.value)
						}
					/>
					<Input
						type="email"
						defaultValue={selection.email}
						placeholder={
							translation.component_input_email_placeholder
						}
						error={errors.email}
						design={"third"}
						onChange={(e: any) =>
							onInputChange("email", e.target.value)
						}
					/>
					<div style={{ marginTop: 16 }}>
						<Text
							type="p2"
							weight="medium"
							style={{
								color: ColorV2.text.neutralMedium,
								marginBottom: 8,
							}}
						>
							Fecha de nacimiento
						</Text>
						<InputBirthday
							defaultValue={
								selection.birthday
									? dayjs(selection.birthday).format(
											"YYYY-MM-DD"
									  )
									: undefined
							}
							placeholder={
								translation.component_input_birthday_placeholder
							}
							error={errors.birthday}
							design={"third"}
							onChange={(e: any) => onInputChange("birthday", e)}
						/>
					</div>
					<CheckboxView>
						<Checkbox
							elementStyle={{ padding: 0, margin: 0 }}
							options={[
								{
									id: "terms",
									Element: (
										<TermsText type="p">
											{
												translation.component_input_terms_text_01
											}
											<Link
												href="https://www.adoptaunabuelo.org/terminos-condiciones"
												target="_blank"
											>
												{
													translation.component_input_terms_text_02
												}
											</Link>{" "}
											{
												translation.component_input_terms_text_03
											}
											<Link
												href="https://www.adoptaunabuelo.org/politica-privacidad"
												target="_blank"
											>
												{
													translation.component_input_terms_text_04
												}
											</Link>
										</TermsText>
									),
									error: errors.terms ? true : false,
								},
							]}
							type={"multiple"}
							onChange={onTermsChange}
						/>
					</CheckboxView>
				</Container>
			</SectionContainer>
		</FatherContainer>
	);
};
export default Personal;
export interface PersonalProps {
	user: UserScheme;
	corporate?: CoorporateScheme;
	onFinish: (result: { data?: UserScheme; error?: string }) => void;
}
